import type { i18n } from "i18next";

import type { CountryKey } from "@/lib/countryProfile";
import { normalizeUiLanguage, type UiLanguage } from "@/lib/uiLanguage";
import en from "@/locales/en.json";
import helpEn from "@/locales/help/en.json";
import helpPtBR from "@/locales/help/pt-BR.json";
import { US_GLOSSARY_EN, US_GLOSSARY_PT_BR } from "@/locales/overlays/usGlossary";
import ptBR from "@/locales/pt-BR.json";

const BASE: Record<UiLanguage, object> = {
  en,
  "pt-BR": ptBR,
};

const HELP: Record<UiLanguage, object> = {
  en: helpEn,
  "pt-BR": helpPtBR,
};

const US_OVERLAY: Record<UiLanguage, object> = {
  en: US_GLOSSARY_EN,
  "pt-BR": US_GLOSSARY_PT_BR,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function mergeOverlay(
  base: Record<string, unknown>,
  overlay: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    const existing = result[key];
    if (isPlainObject(existing) && isPlainObject(value)) {
      result[key] = mergeOverlay(existing, value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/** Fresh translation tree for the active UI language + company country. Does not mutate locale JSON. */
export function buildCountryTranslationResources(
  language: string,
  countryKey: CountryKey,
): Record<string, unknown> {
  const lng = normalizeUiLanguage(language);
  const cloned = cloneJson({
    ...(BASE[lng] as Record<string, unknown>),
    help: HELP[lng],
  });
  switch (countryKey) {
    case "BR":
      return cloned;
    case "US":
      return mergeOverlay(cloned, cloneJson(US_OVERLAY[lng] as Record<string, unknown>));
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function applyCountryLocaleResources(
  instance: i18n,
  language: string,
  countryKey: CountryKey,
): void {
  const lng = normalizeUiLanguage(language);
  const resources = buildCountryTranslationResources(lng, countryKey);
  if (instance.hasResourceBundle(lng, "translation")) {
    instance.removeResourceBundle(lng, "translation");
  }
  instance.addResourceBundle(lng, "translation", resources, true, true);
}
